import React, { useState, useRef, useEffect } from 'react';
import { Download, Shuffle, Palette, User, Shirt, Crown, X } from 'lucide-react';

interface CharacterConfig {
  bodyType: string;
  skinColor: string;
  hairStyle: string;
  hairColor: string;
  eyeStyle: string;
  eyeColor: string;
  outfit: string;
  outfitColor: string;
  accessory: string;
  accessoryColor: string;
  gender: string;
}

interface AvatarCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: CharacterConfig, imageData: string) => void;
  initialConfig?: CharacterConfig;
}

const AvatarCreator: React.FC<AvatarCreatorProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialConfig 
}) => {
  const [character, setCharacter] = useState<CharacterConfig>(initialConfig || {
    bodyType: 'normal',
    skinColor: '#FFD4B3',
    hairStyle: 'short',
    hairColor: '#333333',
    eyeStyle: 'normal',
    eyeColor: '#4A90E2',
    outfit: 'casual',
    outfitColor: '#2E4A54',
    accessory: 'none',
    accessoryColor: '#79804D',
    gender: 'neutral'
  });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [pixelSize] = useState(4);
  const [isAnimating, setIsAnimating] = useState(false);

  // OWT Color palettes
  const skinColors = ['#FFD4B3', '#F5C99B', '#D4A574', '#B87A5B', '#8D5A3D'];
  const hairColors = ['#2D2920', '#69573B', '#79804D', '#3E482A', '#2E4A54', '#384634'];
  const eyeColors = ['#2E4A54', '#79804D', '#3E482A', '#69573B', '#7291A3'];
  const outfitColors = ['#2E4A54', '#79804D', '#3E482A', '#69573B', '#7291A3', '#808D5E'];

  const hairStyles = ['short', 'long', 'spiky', 'ponytail', 'mohawk', 'bald'];
  const outfits = ['casual', 'formal', 'warrior', 'mage', 'ninja', 'school'];
  const accessories = ['none', 'glasses', 'hat', 'headband', 'earrings', 'mask'];

  const drawPixel = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string) => {
    ctx.fillStyle = color;
    ctx.fillRect(x * pixelSize, y * pixelSize, pixelSize, pixelSize);
  };

  const drawHead = (ctx: CanvasRenderingContext2D) => {
    const headPattern = [
      [0,0,1,1,1,1,0,0],
      [0,1,1,1,1,1,1,0],
      [1,1,1,1,1,1,1,1],
      [1,1,1,1,1,1,1,1],
      [1,1,1,1,1,1,1,1],
      [1,1,1,1,1,1,1,1],
      [0,1,1,1,1,1,1,0],
      [0,0,1,1,1,1,0,0]
    ];

    const startX = 12;
    const startY = 10;

    headPattern.forEach((row, y) => {
      row.forEach((pixel, x) => {
        if (pixel === 1) {
          drawPixel(ctx, startX + x, startY + y, character.skinColor);
        }
      });
    });
  };

  const drawHair = (ctx: CanvasRenderingContext2D) => {
    const startX = 12;
    const startY = 8;
    
    const hairPatterns: Record<string, number[][]> = {
      short: [
        [0,1,1,1,1,1,1,0],
        [1,1,1,1,1,1,1,1],
        [1,1,1,1,1,1,1,1],
        [1,0,0,0,0,0,0,1]
      ],
      long: [
        [0,1,1,1,1,1,1,0],
        [1,1,1,1,1,1,1,1],
        [1,1,1,1,1,1,1,1],
        [1,1,0,0,0,0,1,1],
        [1,1,0,0,0,0,1,1],
        [1,1,0,0,0,0,1,1]
      ],
      spiky: [
        [0,1,0,1,1,0,1,0],
        [1,1,1,1,1,1,1,1],
        [1,1,1,1,1,1,1,1],
        [1,0,0,0,0,0,0,1]
      ],
      ponytail: [
        [0,1,1,1,1,1,1,0],
        [1,1,1,1,1,1,1,1],
        [1,1,1,1,1,1,1,1],
        [1,0,0,0,0,0,0,1],
        [0,0,0,0,0,1,1,1],
        [0,0,0,0,0,1,1,1]
      ],
      mohawk: [
        [0,0,1,1,1,1,0,0],
        [0,0,1,1,1,1,0,0],
        [0,0,1,1,1,1,0,0],
        [0,0,0,0,0,0,0,0]
      ],
      bald: []
    };

    const pattern = hairPatterns[character.hairStyle] || [];
    pattern.forEach((row, y) => {
      row.forEach((pixel, x) => {
        if (pixel === 1) {
          drawPixel(ctx, startX + x, startY + y, character.hairColor);
        }
      });
    });
  };

  const drawEyes = (ctx: CanvasRenderingContext2D) => {
    const leftEyeX = 14;
    const rightEyeX = 18;
    const eyeY = 13;

    if (character.eyeStyle === 'normal') {
      drawPixel(ctx, leftEyeX, eyeY, character.eyeColor);
      drawPixel(ctx, rightEyeX, eyeY, character.eyeColor);
    } else if (character.eyeStyle === 'big') {
      drawPixel(ctx, leftEyeX, eyeY, character.eyeColor);
      drawPixel(ctx, leftEyeX + 1, eyeY, character.eyeColor);
      drawPixel(ctx, rightEyeX, eyeY, character.eyeColor);
      drawPixel(ctx, rightEyeX + 1, eyeY, character.eyeColor);
    } else if (character.eyeStyle === 'closed') {
      drawPixel(ctx, leftEyeX, eyeY, '#333');
      drawPixel(ctx, leftEyeX + 1, eyeY, '#333');
      drawPixel(ctx, rightEyeX, eyeY, '#333');
      drawPixel(ctx, rightEyeX + 1, eyeY, '#333');
    }
  };

  const drawBody = (ctx: CanvasRenderingContext2D) => {
    const bodyPattern = [
      [0,0,1,1,1,1,0,0],
      [0,1,1,1,1,1,1,0],
      [1,1,1,1,1,1,1,1],
      [1,1,1,1,1,1,1,1],
      [1,1,1,1,1,1,1,1],
      [1,1,1,1,1,1,1,1],
      [1,1,1,1,1,1,1,1],
      [1,1,1,1,1,1,1,1]
    ];

    const startX = 12;
    const startY = 18;

    bodyPattern.forEach((row, y) => {
      row.forEach((pixel, x) => {
        if (pixel === 1) {
          drawPixel(ctx, startX + x, startY + y, character.outfitColor);
        }
      });
    });

    if (character.outfit === 'warrior') {
      drawPixel(ctx, startX + 3, startY + 2, '#888');
      drawPixel(ctx, startX + 4, startY + 2, '#888');
    } else if (character.outfit === 'mage') {
      for (let i = 1; i < 7; i++) {
        drawPixel(ctx, startX + i, startY + 7, '#444');
      }
    }
  };

  const drawArms = (ctx: CanvasRenderingContext2D) => {
    const leftArmX = 10;
    const rightArmX = 20;
    const armY = 20;

    for (let i = 0; i < 6; i++) {
      drawPixel(ctx, leftArmX, armY + i, character.skinColor);
      drawPixel(ctx, leftArmX + 1, armY + i, character.skinColor);
      drawPixel(ctx, rightArmX, armY + i, character.skinColor);
      drawPixel(ctx, rightArmX + 1, armY + i, character.skinColor);
    }
  };

  const drawLegs = (ctx: CanvasRenderingContext2D) => {
    const leftLegX = 13;
    const rightLegX = 17;
    const legY = 26;

    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 3; j++) {
        drawPixel(ctx, leftLegX + j, legY + i, '#69573B');
        drawPixel(ctx, rightLegX + j, legY + i, '#69573B');
      }
    }

    for (let i = 0; i < 2; i++) {
      for (let j = 0; j < 3; j++) {
        drawPixel(ctx, leftLegX + j, legY + 4 + i, '#2D2920');
        drawPixel(ctx, rightLegX + j, legY + 4 + i, '#2D2920');
      }
    }
  };

  const drawAccessory = (ctx: CanvasRenderingContext2D) => {
    const startX = 12;
    const startY = 8;

    if (character.accessory === 'glasses') {
      drawPixel(ctx, 13, 13, '#333');
      drawPixel(ctx, 14, 13, '#333');
      drawPixel(ctx, 15, 13, '#333');
      drawPixel(ctx, 16, 13, '#333');
      drawPixel(ctx, 17, 13, '#333');
      drawPixel(ctx, 18, 13, '#333');
      drawPixel(ctx, 19, 13, '#333');
    } else if (character.accessory === 'hat') {
      for (let i = 0; i < 8; i++) {
        drawPixel(ctx, startX + i, startY - 1, character.accessoryColor);
      }
      for (let i = 1; i < 7; i++) {
        drawPixel(ctx, startX + i, startY - 2, character.accessoryColor);
      }
    } else if (character.accessory === 'headband') {
      for (let i = 0; i < 8; i++) {
        drawPixel(ctx, startX + i, startY + 2, character.accessoryColor);
      }
    }
  };

  const drawCharacter = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.imageSmoothingEnabled = false;
    
    // Clear canvas to transparent for PNG export
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    drawBody(ctx);
    drawArms(ctx);
    drawLegs(ctx);
    drawHead(ctx);
    drawEyes(ctx);
    drawHair(ctx);
    drawAccessory(ctx);
  };

  const randomizeCharacter = () => {
    setIsAnimating(true);
    const newCharacter: CharacterConfig = {
      bodyType: 'normal',
      skinColor: skinColors[Math.floor(Math.random() * skinColors.length)],
      hairStyle: hairStyles[Math.floor(Math.random() * hairStyles.length)],
      hairColor: hairColors[Math.floor(Math.random() * hairColors.length)],
      eyeStyle: ['normal', 'big', 'closed'][Math.floor(Math.random() * 3)],
      eyeColor: eyeColors[Math.floor(Math.random() * eyeColors.length)],
      outfit: outfits[Math.floor(Math.random() * outfits.length)],
      outfitColor: outfitColors[Math.floor(Math.random() * outfitColors.length)],
      accessory: accessories[Math.floor(Math.random() * accessories.length)],
      accessoryColor: outfitColors[Math.floor(Math.random() * outfitColors.length)],
      gender: ['neutral', 'male', 'female'][Math.floor(Math.random() * 3)]
    };
    setCharacter(newCharacter);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Export as PNG with transparency
    const imageData = canvas.toDataURL('image/png');
    onSave(character, imageData);
  };

  useEffect(() => {
    if (isOpen) {
      drawCharacter();
    }
  }, [character, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-owt-brown/20">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-owt-dark">Create Your Avatar</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-owt-brown" />
            </button>
          </div>
        </div>
        
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-gray-50 rounded-xl p-6 border border-owt-brown/20">
            <div className="flex justify-center mb-6">
              <div className={`transition-transform duration-300 ${isAnimating ? 'scale-110' : ''}`}>
                <canvas 
                  ref={canvasRef}
                  width={128}
                  height={160}
                  className="border-4 border-owt-brown/30 rounded-lg shadow-lg bg-gray-50"
                  style={{ imageRendering: 'pixelated', width: '256px', height: '320px' }}
                />
              </div>
            </div>
            
            <div className="flex gap-4 justify-center">
              <button
                onClick={randomizeCharacter}
                className="bg-owt-sage text-white px-6 py-3 rounded-lg hover:bg-owt-sage/90 transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <Shuffle className="w-5 h-5" />
                Randomize
              </button>
              <button
                onClick={handleSave}
                className="bg-owt-slate text-white px-6 py-3 rounded-lg hover:bg-owt-slate/90 transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
              >
                <Download className="w-5 h-5" />
                Save Avatar
              </button>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="flex items-center gap-2 text-lg font-semibold text-owt-dark mb-3">
                <User className="w-5 h-5" />
                Skin Color
              </label>
              <div className="flex gap-2 flex-wrap">
                {skinColors.map(color => (
                  <button
                    key={color}
                    onClick={() => setCharacter({...character, skinColor: color})}
                    className={`w-12 h-12 rounded-lg border-4 transition-all duration-200 hover:scale-110 ${
                      character.skinColor === color ? 'border-owt-slate shadow-lg' : 'border-owt-brown/30'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-lg font-semibold text-owt-dark mb-3">
                <Palette className="w-5 h-5" />
                Hair Style
              </label>
              <select 
                value={character.hairStyle}
                onChange={(e) => setCharacter({...character, hairStyle: e.target.value})}
                className="w-full p-3 border-2 border-owt-brown/30 rounded-lg focus:border-owt-slate focus:outline-none transition-colors duration-200 bg-white"
              >
                {hairStyles.map(style => (
                  <option key={style} value={style}>{style.charAt(0).toUpperCase() + style.slice(1)}</option>
                ))}
              </select>
              
              <div className="flex gap-2 flex-wrap mt-3">
                {hairColors.map(color => (
                  <button
                    key={color}
                    onClick={() => setCharacter({...character, hairColor: color})}
                    className={`w-10 h-10 rounded-lg border-4 transition-all duration-200 hover:scale-110 ${
                      character.hairColor === color ? 'border-owt-slate shadow-lg' : 'border-owt-brown/30'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-lg font-semibold text-owt-dark mb-3">
                <Shirt className="w-5 h-5" />
                Outfit
              </label>
              <select 
                value={character.outfit}
                onChange={(e) => setCharacter({...character, outfit: e.target.value})}
                className="w-full p-3 border-2 border-owt-brown/30 rounded-lg focus:border-owt-slate focus:outline-none transition-colors duration-200 bg-white"
              >
                {outfits.map(outfit => (
                  <option key={outfit} value={outfit}>{outfit.charAt(0).toUpperCase() + outfit.slice(1)}</option>
                ))}
              </select>
              
              <div className="flex gap-2 flex-wrap mt-3">
                {outfitColors.map(color => (
                  <button
                    key={color}
                    onClick={() => setCharacter({...character, outfitColor: color})}
                    className={`w-10 h-10 rounded-lg border-4 transition-all duration-200 hover:scale-110 ${
                      character.outfitColor === color ? 'border-owt-slate shadow-lg' : 'border-owt-brown/30'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="flex items-center gap-2 text-lg font-semibold text-owt-dark mb-3">
                <Crown className="w-5 h-5" />
                Accessory
              </label>
              <select 
                value={character.accessory}
                onChange={(e) => setCharacter({...character, accessory: e.target.value})}
                className="w-full p-3 border-2 border-owt-brown/30 rounded-lg focus:border-owt-slate focus:outline-none transition-colors duration-200 bg-white"
              >
                {accessories.map(accessory => (
                  <option key={accessory} value={accessory}>{accessory.charAt(0).toUpperCase() + accessory.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const generateRandomAvatar = (): CharacterConfig => {
  const skinColors = ['#FFD4B3', '#F5C99B', '#D4A574', '#B87A5B', '#8D5A3D'];
  const hairColors = ['#2D2920', '#69573B', '#79804D', '#3E482A', '#2E4A54', '#384634'];
  const eyeColors = ['#2E4A54', '#79804D', '#3E482A', '#69573B', '#7291A3'];
  const outfitColors = ['#2E4A54', '#79804D', '#3E482A', '#69573B', '#7291A3', '#808D5E'];
  const hairStyles = ['short', 'long', 'spiky', 'ponytail', 'mohawk', 'bald'];
  const outfits = ['casual', 'formal', 'warrior', 'mage', 'ninja', 'school'];
  const accessories = ['none', 'glasses', 'hat', 'headband', 'earrings', 'mask'];

  return {
    bodyType: 'normal',
    skinColor: skinColors[Math.floor(Math.random() * skinColors.length)],
    hairStyle: hairStyles[Math.floor(Math.random() * hairStyles.length)],
    hairColor: hairColors[Math.floor(Math.random() * hairColors.length)],
    eyeStyle: ['normal', 'big', 'closed'][Math.floor(Math.random() * 3)],
    eyeColor: eyeColors[Math.floor(Math.random() * eyeColors.length)],
    outfit: outfits[Math.floor(Math.random() * outfits.length)],
    outfitColor: outfitColors[Math.floor(Math.random() * outfitColors.length)],
    accessory: accessories[Math.floor(Math.random() * accessories.length)],
    accessoryColor: outfitColors[Math.floor(Math.random() * outfitColors.length)],
    gender: ['neutral', 'male', 'female'][Math.floor(Math.random() * 3)]
  };
};

export default AvatarCreator;